import { LogLevel } from "@azure/msal-browser";

const DEFAULT_MSAL_CLIENT_ID = "c259b3c0-28af-4bab-86f3-3b660b45644b";
const DEFAULT_MSAL_INTERNAL_API_CLIENT_ID = "ea30fa06-48c9-4eba-baa0-a879bf4057de";
const DEFAULT_MSAL_EXTERNAL_API_CLIENT_ID = DEFAULT_MSAL_CLIENT_ID;
const DEFAULT_MSAL_EXTERNAL_TENANT_ID = "156cc46f-f21b-4dad-92d8-504f5cc9af85";
const DEFAULT_MSAL_INTERNAL_TENANT_ID = "d3a97bf2-cefb-4a69-aacf-185d20d028ea";
const DEFAULT_EXTERNAL_AUTHORITY_HOST = "https://fieldprintcalculator.ciamlogin.com";
const AUTHORITY_HOST = "https://login.microsoftonline.com";
const ACTIVE_ACCOUNT_STORAGE_KEY = "ftmc.msal.activeAccountId";

function readEnv(env, name, fallback) {
  const value = env?.[name];

  if (typeof value !== "string") {
    return fallback;
  }

  const trimmedValue = value.trim();

  return trimmedValue || fallback;
}

function isLocalhostHostname(hostname) {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1"
  );
}

function normalizeRedirectUri(candidate, fallback) {
  if (!candidate) {
    return fallback;
  }

  if (typeof window === "undefined") {
    return candidate;
  }

  try {
    const candidateUrl = new URL(candidate, window.location.origin);
    const currentHostname = window.location.hostname;

    if (
      !isLocalhostHostname(currentHostname) &&
      isLocalhostHostname(candidateUrl.hostname)
    ) {
      return fallback;
    }

    return candidateUrl.toString();
  } catch (error) {
    return fallback;
  }
}

export function buildAuthority(authorityOrTenantId) {
  if (!authorityOrTenantId) {
    return `${DEFAULT_EXTERNAL_AUTHORITY_HOST}/${DEFAULT_MSAL_EXTERNAL_TENANT_ID}`;
  }

  if (/^https?:\/\//i.test(authorityOrTenantId)) {
    return authorityOrTenantId.replace(/\/+$/, "");
  }

  return `${AUTHORITY_HOST}/${authorityOrTenantId}`;
}

export function getMsalSettings(env = process.env) {
  const clientId = readEnv(env, "REACT_APP_MSAL_CLIENT_ID", DEFAULT_MSAL_CLIENT_ID);
  const internalApiClientId = readEnv(
    env,
    "REACT_APP_MSAL_INTERNAL_API_CLIENT_ID",
    readEnv(env, "REACT_APP_MSAL_API_CLIENT_ID", DEFAULT_MSAL_INTERNAL_API_CLIENT_ID)
  );
  const externalApiClientId = readEnv(
    env,
    "REACT_APP_MSAL_EXTERNAL_API_CLIENT_ID",
    clientId || DEFAULT_MSAL_EXTERNAL_API_CLIENT_ID
  );
  // Internal (workforce) sign-in is a *separate* app registration, homed in the FxR/org tenant --
  // not just a different authority of the external app. It happens to be the same app that also
  // exposes the internal API scope (internalApiClientId), which is why the fallback below reuses it,
  // but an explicit override is supported in case that ever changes.
  const internalClientId = readEnv(
    env,
    "REACT_APP_MSAL_INTERNAL_CLIENT_ID",
    internalApiClientId
  );
  const externalAuthority = buildAuthority(
    readEnv(
      env,
      "REACT_APP_MSAL_EXTERNAL_AUTHORITY",
      readEnv(env, "REACT_APP_MSAL_AUTHORITY", buildAuthority())
    )
  );
  const internalAuthority = buildAuthority(
    readEnv(
      env,
      "REACT_APP_MSAL_INTERNAL_AUTHORITY",
      DEFAULT_MSAL_INTERNAL_TENANT_ID
    )
  );
  const externalApiScope = readEnv(
    env,
    "REACT_APP_MSAL_EXTERNAL_API_SCOPE",
    `api://${externalApiClientId}/user_impersonation`
  );
  const internalApiScope = readEnv(
    env,
    "REACT_APP_MSAL_INTERNAL_API_SCOPE",
    readEnv(
      env,
      "REACT_APP_MSAL_API_SCOPE",
      `api://${internalApiClientId}/user_impersonation`
    )
  );
  const defaultRedirectUri =
    typeof window === "undefined"
      ? "http://localhost:3000/login"
      : `${window.location.origin}/login`;
  const redirectUri = normalizeRedirectUri(
    readEnv(
      env,
      "REACT_APP_MSAL_REDIRECT_URI",
      defaultRedirectUri
    ),
    defaultRedirectUri
  );
  const postLogoutRedirectUri = normalizeRedirectUri(
    readEnv(
      env,
      "REACT_APP_MSAL_POST_LOGOUT_REDIRECT_URI",
      redirectUri
    ),
    redirectUri
  );
  const knownAuthorities = Array.from(
    new Set(
      [externalAuthority]
        .map((authority) => {
          try {
            return new URL(authority).hostname;
          } catch (error) {
            return null;
          }
        })
        .filter(Boolean)
    )
  );

  return {
    clientId,
    internalClientId,
    authority: externalAuthority,
    externalAuthority,
    internalAuthority,
    externalApiScope,
    internalApiScope,
    redirectUri,
    postLogoutRedirectUri,
    knownAuthorities,
  };
}

function getBrowserStorage() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
}

function getAccountStorageId(account) {
  return account?.homeAccountId || account?.localAccountId || null;
}

export function rememberActiveMsalAccount(account) {
  const storage = getBrowserStorage();
  const accountId = getAccountStorageId(account);

  if (!storage || !accountId) {
    return;
  }

  storage.setItem(ACTIVE_ACCOUNT_STORAGE_KEY, accountId);
}

export function clearRememberedActiveMsalAccount() {
  const storage = getBrowserStorage();

  if (!storage) {
    return;
  }

  storage.removeItem(ACTIVE_ACCOUNT_STORAGE_KEY);
}

export function setActiveMsalAccount(instance, account) {
  if (!instance || !account) {
    return null;
  }

  instance.setActiveAccount(account);
  rememberActiveMsalAccount(account);
  return account;
}

export function resolveActiveMsalAccount(
  instance,
  accounts = instance?.getAllAccounts?.() || []
) {
  if (!instance) {
    return null;
  }

  const activeAccount = instance.getActiveAccount();

  if (activeAccount) {
    rememberActiveMsalAccount(activeAccount);
    return activeAccount;
  }

  const storage = getBrowserStorage();
  const rememberedAccountId =
    storage?.getItem(ACTIVE_ACCOUNT_STORAGE_KEY)?.trim() || null;

  if (rememberedAccountId) {
    const rememberedAccount =
      accounts.find(
        (account) =>
          account?.homeAccountId === rememberedAccountId ||
          account?.localAccountId === rememberedAccountId
      ) || null;

    if (rememberedAccount) {
      return setActiveMsalAccount(instance, rememberedAccount);
    }
  }

  if (accounts.length === 1) {
    return setActiveMsalAccount(instance, accounts[0]);
  }

  return null;
}

export const msalSettings = getMsalSettings();
export const EXTERNAL_API_SCOPE = msalSettings.externalApiScope;
export const INTERNAL_API_SCOPE = msalSettings.internalApiScope;
export const API_SCOPE = INTERNAL_API_SCOPE;
export const LOGIN_SCOPES = ["openid", "profile", "offline_access"];

export const msalConfig = {
  auth: {
    clientId: msalSettings.clientId,
    authority: msalSettings.authority,
    redirectUri: msalSettings.redirectUri,
    postLogoutRedirectUri: msalSettings.postLogoutRedirectUri,
    knownAuthorities: msalSettings.knownAuthorities,
  },
  cache: {
    cacheLocation: "localStorage",
    // Tokens/accounts survive closing this tab or browser; transient OAuth
    // interaction state stays tab-local so parallel tabs do not block each other.
    temporaryCacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback(logLevel, message, containsPii) {
        if (containsPii) {
          return;
        }

        switch (logLevel) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
      logLevel: LogLevel.Warning,
    },
  },
};

// Internal (workforce) sign-in is a *separate* Entra app registration homed in a different tenant --
// MSAL doesn't allow swapping clientId per-request (only authority), so it needs its own
// PublicClientApplication instance, not just a different `authority` override on the external one.
// Using the external app's clientId against the internal tenant is exactly what produced
// AADSTS650059 ("application is not configured for use in tenant ...").
export const internalMsalConfig = {
  auth: {
    clientId: msalSettings.internalClientId,
    authority: msalSettings.internalAuthority,
    redirectUri: msalSettings.redirectUri,
    postLogoutRedirectUri: msalSettings.postLogoutRedirectUri,
  },
  cache: {
    cacheLocation: "localStorage",
    temporaryCacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
  system: msalConfig.system,
};

// Both instances share `cacheLocation: "localStorage"` -- MSAL's account cache entries aren't keyed by
// clientId (only token entries are), so accounts logged in via either instance are visible to both.
// This is what lets resolveActiveMsalAccount() below work regardless of which instance a user signed
// in through.
//
// Construction is deliberately NOT done here at module scope: `new PublicClientApplication(...)` throws
// in test/SSR environments without a real WebCrypto implementation (Jest/jsdom included), and this module
// is imported by test files that never touch the network. index.js constructs both real instances once at
// app boot and calls registerMsalInstances() immediately after, before rendering.
let externalMsalInstance = null;
let internalMsalInstanceRef = null;

export function registerMsalInstances(external, internal) {
  externalMsalInstance = external;
  internalMsalInstanceRef = internal;
}

export function enableMsalAccountStorageEvents() {
  // MsalProvider owns the external instance even for cached workforce users.
  // Both instances must observe shared-cache changes from other browser tabs.
  new Set([externalMsalInstance, internalMsalInstanceRef]).forEach((instance) => {
    instance?.enableAccountStorageEvents?.();
  });
}

export function getExternalMsalInstance() {
  return externalMsalInstance;
}

export function getInternalMsalInstance() {
  return internalMsalInstanceRef;
}

function getAuthorityHostname(authority) {
  if (!authority) {
    return null;
  }

  try {
    return new URL(authority).hostname.toLowerCase();
  } catch (error) {
    return null;
  }
}

function getAuthorityTenantId(authority) {
  if (!authority) {
    return null;
  }

  try {
    const segments = new URL(authority).pathname.split("/").filter(Boolean);
    return segments[0] || null;
  } catch (error) {
    return null;
  }
}

export function resolveAuthorityForAccount(
  account,
  defaultAuthority = msalSettings.authority
) {
  const tenantId = account?.tenantId || account?.idTokenClaims?.tid || null;
  const accountEnvironment = account?.environment?.toLowerCase() || null;

  if (!tenantId) {
    return defaultAuthority;
  }

  const defaultAuthorityTenantId = getAuthorityTenantId(defaultAuthority);
  const defaultAuthorityHostname = getAuthorityHostname(defaultAuthority);

  if (
    defaultAuthorityTenantId === tenantId ||
    (accountEnvironment && defaultAuthorityHostname === accountEnvironment)
  ) {
    return defaultAuthority;
  }

  return buildAuthority(tenantId);
}

export function isExternalAccount(account) {
  const tenantId = account?.tenantId || account?.idTokenClaims?.tid || null;
  return tenantId === getAuthorityTenantId(msalSettings.externalAuthority);
}

export function isInternalAccount(account) {
  const tenantId = account?.tenantId || account?.idTokenClaims?.tid || null;
  return tenantId === getAuthorityTenantId(msalSettings.internalAuthority);
}

export function isAccountAllowedForPortal(account, portal) {
  if (!account) {
    return false;
  }

  return portal === "grower"
    ? isExternalAccount(account)
    : isInternalAccount(account);
}

export function resolveActiveMsalAccountForPortal(
  instance,
  accounts = instance?.getAllAccounts?.() || [],
  portal = "grower"
) {
  const portalAccounts = accounts.filter((account) =>
    isAccountAllowedForPortal(account, portal)
  );
  const activeAccount = instance?.getActiveAccount?.() || null;

  if (activeAccount && !isAccountAllowedForPortal(activeAccount, portal)) {
    instance.setActiveAccount(null);
    clearRememberedActiveMsalAccount();
  }

  return resolveActiveMsalAccount(instance, portalAccounts);
}

// Any actual token operation (acquireTokenSilent/Popup/Redirect, loginPopup, logoutPopup) must run on
// the PublicClientApplication instance matching the account's own app registration -- token cache
// entries ARE keyed by clientId, unlike account entries. Always route through this instead of assuming
// the msal-react context instance (which is always the external one).
export function getMsalInstanceForAccount(account) {
  return isExternalAccount(account) ? externalMsalInstance : internalMsalInstanceRef;
}

export function resolveApiScopeForAccount(account) {
  return isExternalAccount(account) ? EXTERNAL_API_SCOPE : INTERNAL_API_SCOPE;
}

export function resolveApiScopesForAccount(account) {
  return [resolveApiScopeForAccount(account)];
}

export function buildInteractiveLoginRequest(overrides = {}) {
  return {
    scopes: LOGIN_SCOPES,
    prompt: "select_account",
    redirectUri: msalSettings.redirectUri,
    ...overrides,
  };
}

export function buildExternalLoginRequest(overrides = {}) {
  return buildInteractiveLoginRequest({
    authority: msalSettings.externalAuthority,
    ...overrides,
  });
}

export function buildInternalLoginRequest(overrides = {}) {
  return buildInteractiveLoginRequest({
    authority: msalSettings.internalAuthority,
    ...overrides,
  });
}

export function buildTokenRequest({
  scopes,
  account = null,
  forceRefresh = false,
  authority,
} = {}) {
  return {
    scopes:
      Array.isArray(scopes) && scopes.length > 0
        ? scopes
        : resolveApiScopesForAccount(account),
    account,
    forceRefresh,
    authority: authority || resolveAuthorityForAccount(account),
    redirectUri: msalSettings.redirectUri,
  };
}

export const loginRequest = buildExternalLoginRequest();

export function isDomainMismatchLoginError(error) {
  const message = [
    error?.errorMessage,
    error?.message,
    error?.errorCode,
    error?.subError,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    message.includes("AADSTS500208") ||
    /valid login domain for the account type/i.test(message)
  );
}

export function isResourceAccountTypeMismatchError(error) {
  const message = [
    error?.errorMessage,
    error?.message,
    error?.errorCode,
    error?.subError,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    message.includes("AADSTS500207") ||
    /account type can't be used for the resource/i.test(message)
  );
}

export function getMsalLoginErrorMessage(error) {
  if (isDomainMismatchLoginError(error)) {
    return "This account does not match the sign-in option you selected. Try the other Microsoft sign-in button.";
  }

  if (isResourceAccountTypeMismatchError(error)) {
    return "Microsoft sign-in succeeded, but this account type is not allowed to request the configured API resource. The external user flow and backend API scope likely need to be aligned.";
  }

  return (
    error?.message ||
    error?.errorMessage ||
    "Microsoft sign-in failed. Please try again."
  );
}

function clearActiveMsalLoginSelection(instance) {
  clearRememberedActiveMsalAccount();

  if (typeof instance?.setActiveAccount === "function") {
    instance.setActiveAccount(null);
  }
}

async function loginWithMicrosoftAuthority(instance, authority, overrides = {}) {
  clearActiveMsalLoginSelection(instance);

  const finalizeAuthResponse = (response) => {
    if (response?.account) {
      setActiveMsalAccount(instance, response.account);
    }

    return response;
  };

  return finalizeAuthResponse(
    await instance.loginPopup(
      buildInteractiveLoginRequest({
        ...overrides,
        authority,
      })
    )
  );
}

export async function loginWithExternalMicrosoft(overrides = {}) {
  return loginWithMicrosoftAuthority(
    getExternalMsalInstance(),
    msalSettings.externalAuthority,
    overrides
  );
}

export async function loginWithInternalMicrosoft(overrides = {}) {
  return loginWithMicrosoftAuthority(
    getInternalMsalInstance(),
    msalSettings.internalAuthority,
    overrides
  );
}
